import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { A11yModule } from '@angular/cdk/a11y';
import { CollectionReportFileCardComponent } from '../collection-report-file-card/collection-report-file-card.component';

export type FileValidationIssueKind = 'RESOLVABLE' | 'PERMANENT';

export interface FileValidationIssue {
  readonly fileId: string;
  readonly fileName: string;
  readonly sizeBytes: number | null;
  readonly kind: FileValidationIssueKind;
  readonly reason: string;
}

@Component({
  selector: 'lib-file-validation-issues-dialog',
  imports: [
    CommonModule,
    A11yModule,
    TranslocoModule,
    CollectionReportFileCardComponent,
  ],
  providers: [
    provideTranslocoScope({
      scope: 'mzic-collection-flow',
      alias: 'lang',
    }),
  ],
  templateUrl: './file-validation-issues-dialog.component.html',
  styleUrl: './file-validation-issues-dialog.component.scss',
})
export class FileValidationIssuesDialogComponent {
  @Input() issues: readonly FileValidationIssue[] = [];
  @Output() readonly removeFiles = new EventEmitter<readonly string[]>();
  @Output() readonly backRequested = new EventEmitter<void>();
  @Output() readonly reviewReports = new EventEmitter<void>();

  get permanentFileIds(): readonly string[] {
    return this.issues.flatMap((issue) =>
      issue.kind === 'PERMANENT' ? [issue.fileId] : [],
    );
  }

  get hasPermanentIssues(): boolean {
    return this.permanentFileIds.length > 0;
  }

  get reviewActionKey(): string {
    return this.issues.length === 1
      ? 'file_validation_review_report'
      : 'file_validation_review_reports';
  }

  get removeActionKey(): string {
    return this.permanentFileIds.length === 1
      ? 'file_validation_remove_unusable_report'
      : 'file_validation_remove_unusable';
  }

  get titleKey(): string {
    if (this.hasPermanentIssues) {
      return this.permanentFileIds.length === 1
        ? 'file_validation_permanent_title'
        : 'file_validation_permanent_title_plural';
    }
    return this.issues.length === 1
      ? 'file_validation_issues_title_singular'
      : 'file_validation_issues_title';
  }

  get messageKey(): string {
    return this.hasPermanentIssues
      ? 'file_validation_permanent_message'
      : 'file_validation_issues_message';
  }

  onRemoveFiles(): void {
    if (this.permanentFileIds.length > 0) {
      this.removeFiles.emit(this.permanentFileIds);
    }
  }

  onBack(): void {
    this.backRequested.emit();
  }

  onReviewReports(): void {
    this.reviewReports.emit();
  }
}
